/** What a dialog over the page puts on the stack: the node it was portaled into, and how it closes. */
export interface Layer {
  readonly node: { toggleAttribute(name: string, force: boolean): unknown } | null;
  close(): void;
}

/**
 * The dialogs open over the page, bottom first. Only the top one is live: the
 * ones under it are inert, so Tab and a press stay in the top one, and Escape
 * goes to it alone.
 */
export class LayerStack<L extends Layer = Layer> {
  readonly #layers: L[] = [];

  get top(): L | null {
    return this.#layers.at(-1) ?? null;
  }

  get size(): number {
    return this.#layers.length;
  }

  open(layer: L): void {
    this.#layers.push(layer);
    this.#settle();
  }

  /** Takes the layer off, and answers whether it was the top one. */
  close(layer: L): boolean {
    const at = this.#layers.indexOf(layer);
    if (at < 0) return false;
    const top = at === this.#layers.length - 1;
    this.#layers.splice(at, 1);
    this.#settle();
    return top;
  }

  #settle(): void {
    const last = this.#layers.length - 1;
    this.#layers.forEach((layer, i) => {
      layer.node?.toggleAttribute("inert", i < last);
    });
  }
}
